import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Building2, Check, ChevronDown, User } from 'lucide-react';
import {
  orgKindLabelKey,
  roleContextKey,
  switcherOffered,
  type RoleContext,
} from '../../lib/auth/roleContexts';
import { useT } from '../../i18n';

// ═══════════════════════════════════════════════════════════════════
// «BYTT VISNING» — the portal's context switcher (owner, 2026-10-20/21).
//
// Grew out of the organization switcher (#752), which switched between
// organizations and back to the personal home. What the owner asked for:
// switching easily between the contexts a person holds — «book as a player,
// as the club, as the centre». So it lists «Spiller» and one entry per
// organization, each NAMED BY THE ORGANIZATION with its kind beneath, as
// #752 did. Roles are never listed: within an organization they combine into
// the capability union the server decides on every request.
//
// ── WHAT IT LISTS, AND WHERE THAT COMES FROM ─────────────────────
//
// `lib/auth/roleContexts.ts` builds the list from the server's answers: the
// organizations from `/auth/scopes` (where may I ACT — never `/me/clubs`, the
// 2026-10-15 ruling still holds), «Spiller» from `/me/player`. This component
// draws that list and nothing else; it cannot add a context, and it never
// names a role. There is no `/sa` entry: that door is Entra's (#1002).
//
// ── IT SELECTS A CONTEXT AND GRANTS NOTHING ───────────────────────
//
// Entering one sets client state — the surface and which organization the
// requests address. The server re-reads authority on every request. Hiding a
// destination is a courtesy, never a boundary.
//
// ── ABSENT WITH NOTHING TO SWITCH BETWEEN ─────────────────────────
//
// One context is not a choice: a person who administers one centre and does
// not play sees no control at all.
// ═══════════════════════════════════════════════════════════════════

export interface RoleSwitcherProps {
  /** Everything this account holds — `roleContexts()`, from the server's answers. */
  contexts: RoleContext[];
  /** `roleContextKey` of the context on screen, or null when it is none of them. */
  currentKey: string | null;
  /** Enter an organization's workspace. The caller selects surface and scope. */
  onEnterOrganization: (tenantId: string) => void;
  /** Go to the player app. */
  onPlayer?: () => void;
}

/** The first line: «Spiller», or the organization's own name. */
function nameOf(c: RoleContext, t: ReturnType<typeof useT>): string {
  return c.kind === 'player' ? t('roleSwitcher.player') : c.name;
}

/** The second line: where it is — the player app, or the organization's kind. */
function kindOf(c: RoleContext, t: ReturnType<typeof useT>): string {
  if (c.kind === 'player') return t('roleSwitcher.place.player');
  const key = orgKindLabelKey(c.orgKind);
  return key ? t(key) : '';
}

function iconOf(c: RoleContext): ReactNode {
  if (c.kind === 'player') return <User size={14} className="text-ink-3" aria-hidden="true" />;
  return <Building2 size={14} className="text-ink-3" aria-hidden="true" />;
}

export function RoleSwitcher({
  contexts,
  currentKey,
  onEnterOrganization,
  onPlayer,
}: RoleSwitcherProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The trigger, so closing the menu from the keyboard hands focus back to
  // what opened it rather than to <body> (WCAG 2.4.3, WAI-ARIA menu button;
  // `NavMenu` does the same, #1300).
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // A capture-phase outside click and Escape, both registered only while
  // open — the same idiom as `NavMenu`. A menu over the page, not a modal.
  useEffect(() => {
    if (!open) return undefined;
    const clickOutside = (e: Event) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', clickOutside, true);
    document.addEventListener('touchstart', clickOutside, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', clickOutside, true);
      document.removeEventListener('touchstart', clickOutside, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A context with no way to reach it is not offered: the player app without
  // `onPlayer` (the caller withholds it where there is no way back).
  const reachable = contexts.filter((c) => c.kind !== 'player' || !!onPlayer);
  if (!switcherOffered(reachable)) return null;

  const current = reachable.find((c) => roleContextKey(c) === currentKey) ?? null;

  const choose = (c: RoleContext) => {
    setOpen(false);
    // The chosen item unmounts with the menu; without this, focus would fall
    // to <body>. A switch that remounts the surface moves it anyway.
    triggerRef.current?.focus();
    // Re-entering the context already on screen is a no-op rather than a
    // remount: the store is keyed on the scope, so «switching» to where you
    // already are would throw away every loaded view for nothing.
    if (roleContextKey(c) === currentKey) return;
    if (c.kind === 'organization') onEnterOrganization(c.tenantId);
    else onPlayer?.();
  };

  return (
    // Below `sm` the header's right-hand group shrinks, and this is the item
    // that gives way: the wrapper may get narrower than its content, the
    // button follows it, and the organization's name truncates with an
    // ellipsis (owner decision 2026-11-02). Its menu is 18rem, wider than
    // the space left of a phone-width trigger, so below `sm` the wrapper is
    // `static` and the menu hangs from the HEADER (sticky, so positioned)
    // with a margin on each side instead of from the trigger's right edge,
    // which put it off the left of the screen. From `sm` to `lg` (#1294) it
    // hangs from the trigger's LEFT edge instead (`max-lg:left-0`): the
    // trigger now sits at the start of the right-hand group, and 18rem from
    // its right edge reached past the left of a tablet screen. From `lg` up
    // it is what it always was.
    // THE FLOOR, 26 px: the button's padding (4 + 4), gap (4) and chevron (14).
    // Without it, at 320 px the wrapper shrank to 8 px, the chevron slid under
    // the club button and the control was invisible (#1292 real-browser walk).
    // At the floor the name is 0 px wide and the chevron alone is the target;
    // the accessible name «Bytt visning» does not depend on the visible text.
    // AT EVERY WIDTH SINCE #1294 (owner decision 2026-11-02): from 640 to
    // 1023 px the header overflowed with two or more organizations, so the
    // wrapper may shrink to the same floor everywhere and the name truncates.
    // Where it fits, it does not shrink, and nothing changes.
    <div
      className="relative min-w-[26px] max-sm:static"
      ref={containerRef}
      data-testid="role-switcher"
    >
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-ink hover:bg-bg-soft transition-colors max-w-full max-sm:gap-1 max-sm:px-1"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('roleSwitcher.label')}
        onClick={() => setOpen((o) => !o)}
        data-testid="role-switcher-button"
      >
        {/* Below `sm` the kind icon makes room for the name: on a 360 px
            screen with two organizations there is space for one of them, and
            the name is what says where you are. */}
        <span className="contents max-sm:hidden">
          {current ? iconOf(current) : iconOf({ kind: 'player' })}
        </span>
        <span className="flex flex-col items-start leading-tight min-w-0">
          <span className="max-w-[min(12rem,100%)] truncate max-sm:max-w-full">
            {current ? nameOf(current, t) : t('roleSwitcher.label')}
          </span>
          {current && kindOf(current, t) && (
            <span className="hidden md:block max-w-[min(12rem,100%)] truncate text-[10px] text-ink-3">
              {kindOf(current, t)}
            </span>
          )}
        </span>
        <ChevronDown size={14} aria-hidden="true" className="flex-shrink-0" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('roleSwitcher.label')}
          className="absolute top-full right-0 mt-1 w-72 card-elev py-1.5 px-1.5 max-lg:left-0 max-lg:right-auto max-sm:left-3 max-sm:right-3 max-sm:w-auto"
          style={{ zIndex: 50 }}
          data-testid="role-switcher-menu"
        >
          <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {t('roleSwitcher.label')}
          </div>
          {reachable.map((c) => {
            const key = roleContextKey(c);
            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-bg-soft"
                onClick={() => choose(c)}
                data-testid={`role-switcher-item-${key}`}
              >
                {iconOf(c)}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-ink">{nameOf(c, t)}</span>
                  {kindOf(c, t) && (
                    <span className="block truncate text-[11px] text-ink-3">{kindOf(c, t)}</span>
                  )}
                </span>
                {key === currentKey && (
                  <Check size={14} aria-hidden="true" data-testid="role-switcher-current" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
